'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { User, Users, CheckSquare, CreditCard, ChevronRight, ChevronLeft, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

// ─── Benefit definitions ────────────────────────────────────────────────────
const ALL_BENEFITS = [
  { id: 'b-uniform', label: 'School Uniform', defaultChecked: true },
  { id: 'b-stationery', label: 'Stationery', defaultChecked: true },
  { id: 'b-sanitary', label: 'Sanitary Pads', defaultChecked: false, girlsOnly: true, minGrade: 5 },
  { id: 'b-medical', label: 'Emergency Medical Advance', defaultChecked: false },
  { id: 'b-lunchbox', label: 'School Trip Lunch Box', defaultChecked: false },
  { id: 'b-data', label: 'Data / Library Transport', defaultChecked: false },
  { id: 'b-birthday', label: 'Birthday Outings', defaultChecked: false },
  { id: 'b-progress', label: 'Teacher Progress Check', defaultChecked: false },
  { id: 'b-postmatric', label: 'Post-Matric Support', defaultChecked: false, minGrade: 10 },
  { id: 'b-skills', label: 'Skills Development', defaultChecked: false },
  { id: 'b-lifeskills', label: 'Life Skills Development Program', defaultChecked: false },
  { id: 'b-safety', label: 'On-Site School Safety', defaultChecked: true },
  { id: 'b-death', label: 'Death of a Parent/Guardian Benefit', defaultChecked: false },
];

const GRADES = ['Grade R', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
  'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

function gradeNumber(grade: string): number {
  if (grade === 'Grade R') return 0;
  const n = parseInt(grade.replace('Grade ', ''), 10);
  return isNaN(n) ? 0 : n;
}

type FormData = {
  // Parent
  parentFirstName: string;
  parentLastName: string;
  parentPhone: string;
  parentEmail: string;
  parentIdNumber: string;
  relationship: string;
  // Child
  childFirstName: string;
  childLastName: string;
  childGrade: string;
  childGender: string;
  childSchool: string;
  childDateOfBirth: string;
  // Benefits
  selectedBenefits: string[];
  // Payment
  paymentMethod: string;
};

const steps = [
  { id: 'step-parent', label: 'Parent/Guardian', icon: User },
  { id: 'step-child', label: 'Child Details', icon: Users },
  { id: 'step-benefits', label: 'Select Benefits', icon: CheckSquare },
  { id: 'step-payment', label: 'Payment', icon: CreditCard },
];

export default function EnrollmentFormClient() {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      parentFirstName: '',
      parentLastName: '',
      parentPhone: '',
      parentEmail: '',
      parentIdNumber: '',
      relationship: '',
      childFirstName: '',
      childLastName: '',
      childGrade: '',
      childGender: '',
      childSchool: '',
      childDateOfBirth: '',
      selectedBenefits: ['b-uniform', 'b-stationery', 'b-safety'],
      paymentMethod: 'payfast',
    },
  });

  const watchedGender = watch('childGender');
  const watchedGrade = watch('childGrade');
  const watchedBenefits = watch('selectedBenefits');

  const gradeNum = gradeNumber(watchedGrade);

  function isBenefitVisible(b: typeof ALL_BENEFITS[0]): boolean {
    if (b.girlsOnly && (watchedGender === 'Male' || gradeNum < 5)) return false;
    if (b.minGrade && b.id === 'b-postmatric' && gradeNum < 10) return false;
    return true;
  }

  const visibleBenefits = ALL_BENEFITS.filter(isBenefitVisible);

  function toggleBenefit(id: string) {
    const current = getValues('selectedBenefits');
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    setValue('selectedBenefits', next);
  }

  async function nextStep() {
    let fieldsToValidate: (keyof FormData)[] = [];
    if (currentStep === 0) {
      fieldsToValidate = ['parentFirstName', 'parentLastName', 'parentPhone', 'parentEmail', 'relationship'];
    } else if (currentStep === 1) {
      fieldsToValidate = ['childFirstName', 'childLastName', 'childGrade', 'childGender', 'childSchool'];
    }
    const valid = await trigger(fieldsToValidate);
    if (valid) setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  }

  function prevStep() {
    setCurrentStep((s) => Math.max(s - 1, 0));
  }

  async function onSubmit(data: FormData) {
    // BACKEND INTEGRATION: POST /api/enrollments with data
    const visibleIds = visibleBenefits.map((b) => b.id);
    const filtered = data.selectedBenefits.filter((id) => visibleIds.includes(id));
    if (filtered.length < 3) {
      toast.error('Please select at least 3 benefits to proceed.');
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 2000));
    setSubmitting(false);
    setSubmitted(true);
    toast.success('Enrollment submitted! We will contact you within 24 hours.');
  }

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={40} className="text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-3">Enrollment Submitted!</h1>
        <p className="text-muted-foreground leading-relaxed mb-6">
          Thank you for enrolling your child with Luvare Collective Pty Ltd. A confirmation
          will be sent to your email and WhatsApp within 24 hours. Your R250/month
          membership starts on payment confirmation.
        </p>
        <div className="bg-secondary rounded-xl p-5 text-left mb-6">
          <p className="text-sm font-semibold text-foreground mb-1">What happens next?</p>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>We verify your details within 24 hours</li>
            <li>Payment link sent via WhatsApp/email</li>
            <li>Benefits activate from day 1 of first payment</li>
            <li>Uniform and stationery after 12 months</li>
          </ul>
        </div>
        <a
          href="https://wa.me/27658947355"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity"
        >
          Chat on WhatsApp for queries
        </a>
      </div>
    );
  }

  const selectedCount = watchedBenefits.filter((id) =>
    visibleBenefits.map((b) => b.id).includes(id)
  ).length;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Enroll Your Child</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Complete all 4 steps to activate your R250/month Luvare membership
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center mb-8 overflow-x-auto pb-2">
        {steps.map((step, idx) => {
          const StepIcon = step.icon;
          const isActive = idx === currentStep;
          const isCompleted = idx < currentStep;
          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center gap-1.5 min-w-[64px]">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    isCompleted
                      ? 'step-completed'
                      : isActive
                      ? 'step-active shadow-lg shadow-primary/30'
                      : 'step-inactive'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle2 size={18} />
                  ) : (
                    <StepIcon size={18} />
                  )}
                </div>
                <span
                  className={`text-[10px] font-semibold text-center leading-tight ${
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 rounded-full transition-colors duration-300 ${
                    idx < currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Form card */}
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ─── STEP 0: Parent/Guardian ─────────────────────────── */}
          {currentStep === 0 && (
            <div className="p-6">
              <h2 className="text-lg font-bold mb-5 flex items-center gap-2">
                <User size={18} className="text-primary" />
                Parent / Guardian Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('parentFirstName', { required: 'First name is required' })}
                    placeholder="e.g. Nomsa"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  />
                  {errors.parentFirstName && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.parentFirstName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('parentLastName', { required: 'Last name is required' })}
                    placeholder="e.g. Dlamini"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  />
                  {errors.parentLastName && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.parentLastName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Cell Number <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Used for WhatsApp updates and payment links
                  </p>
                  <input
                    {...register('parentPhone', {
                      required: 'Phone number is required',
                      pattern: { value: /^0[6-8][0-9]{8}$/, message: 'Enter a valid SA mobile number' },
                    })}
                    placeholder="e.g. 0712055570"
                    type="tel"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  />
                  {errors.parentPhone && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.parentPhone.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    For enrollment confirmation and receipts
                  </p>
                  <input
                    {...register('parentEmail', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                    placeholder="e.g. nomsa@gmail.com"
                    type="email"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  />
                  {errors.parentEmail && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.parentEmail.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    SA ID Number
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Optional — used for Death Benefit verification after 6 years
                  </p>
                  <input
                    {...register('parentIdNumber')}
                    placeholder="e.g. 8501015800086"
                    maxLength={13}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors font-mono"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Relationship to Child <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('relationship', { required: 'Please select your relationship' })}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  >
                    <option value="">Select relationship</option>
                    <option value="Mother">Mother</option>
                    <option value="Father">Father</option>
                    <option value="Guardian">Guardian</option>
                    <option value="Grandparent">Grandparent</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.relationship && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.relationship.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ─── STEP 1: Child Details ────────────────────────────── */}
          {currentStep === 1 && (
            <div className="p-6">
              <h2 className="text-lg font-bold mb-5 flex items-center gap-2">
                <Users size={18} className="text-primary" />
                Child Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Child's First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('childFirstName', { required: 'Child first name is required' })}
                    placeholder="e.g. Ayanda"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  />
                  {errors.childFirstName && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.childFirstName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Child's Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('childLastName', { required: 'Child last name is required' })}
                    placeholder="e.g. Nkosi"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  />
                  {errors.childLastName && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.childLastName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Date of Birth
                  </label>
                  <input
                    {...register('childDateOfBirth')}
                    type="date"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Determines eligibility for Sanitary Pads benefit
                  </p>
                  <select
                    {...register('childGender', { required: 'Please select gender' })}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  >
                    <option value="">Select gender</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                  </select>
                  {errors.childGender && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.childGender.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Current Grade <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Affects Post-Matric Support eligibility (Grade 10+)
                  </p>
                  <select
                    {...register('childGrade', { required: 'Please select a grade' })}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  >
                    <option value="">Select grade</option>
                    {GRADES.map((g) => (
                      <option key={`grade-${g}`} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  {errors.childGrade && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.childGrade.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    School Name <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Full official school name for our liaison records
                  </p>
                  <input
                    {...register('childSchool', { required: 'School name is required' })}
                    placeholder="e.g. Ekurhuleni Primary School"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  />
                  {errors.childSchool && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {errors.childSchool.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ─── STEP 2: Benefit Selection ────────────────────────── */}
          {currentStep === 2 && (
            <div className="p-6">
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <CheckSquare size={18} className="text-primary" />
                  Select Benefits to Activate
                </h2>
                <span
                  className={`text-xs font-bold px-3 py-1 rounded-full tabular-nums ${
                    selectedCount >= 3
                      ? 'bg-emerald-100 text-emerald-700' :'bg-red-100 text-red-600'
                  }`}
                >
                  {selectedCount} / min 3
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Select 3 or more benefits to activate for this child. Some benefits are
                automatically shown based on grade and gender.
              </p>

              {selectedCount < 3 && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 mb-4">
                  <AlertCircle size={15} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-600 font-medium">
                    Please select at least 3 benefits to proceed.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-3">
                {visibleBenefits.map((benefit) => {
                  const isChecked = watchedBenefits.includes(benefit.id);
                  const isDefault = ['b-uniform', 'b-stationery', 'b-safety'].includes(benefit.id);
                  return (
                    <label
                      key={benefit.id}
                      className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                        isChecked
                          ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/40 hover:bg-secondary/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleBenefit(benefit.id)}
                        className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0"
                      />
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-semibold text-foreground">
                          {benefit.label}
                          {isDefault && (
                            <span className="ml-2 text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              Recommended
                            </span>
                          )}
                        </span>
                        {benefit.girlsOnly && (
                          <span className="text-xs text-pink-600">Girls only · Grade 5+</span>
                        )}
                        {benefit.id === 'b-postmatric' && (
                          <span className="text-xs text-emerald-600">Grade 10 and above</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── STEP 3: Payment ─────────────────────────────────── */}
          {currentStep === 3 && (
            <div className="p-6">
              <h2 className="text-lg font-bold mb-5 flex items-center gap-2">
                <CreditCard size={18} className="text-primary" />
                Payment Summary
              </h2>

              {/* Summary */}
              <div className="bg-secondary rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Child enrolled</span>
                  <span className="text-sm font-semibold">
                    {watch('childFirstName')} {watch('childLastName')}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">School</span>
                  <span className="text-sm font-semibold">{watch('childSchool') || '—'}</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Grade</span>
                  <span className="text-sm font-semibold">{watch('childGrade') || '—'}</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Benefits selected</span>
                  <span className="text-sm font-semibold tabular-nums">
                    {selectedCount} of {visibleBenefits.length}
                  </span>
                </div>
                <div className="border-t border-border pt-3 flex items-center justify-between">
                  <span className="text-base font-bold text-foreground">Monthly subscription</span>
                  <span className="text-2xl font-bold text-primary tabular-nums">R250</span>
                </div>
              </div>

              {/* Payment method */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-3">
                  Payment Method
                </label>
                <div className="flex flex-col gap-3">
                  {[
                    { id: 'pm-payfast', value: 'payfast', label: 'PayFast', desc: 'Credit/debit card, EFT, Instant EFT' },
                    { id: 'pm-paygate', value: 'paygate', label: 'PayGate', desc: 'Visa, Mastercard, Amex' },
                    { id: 'pm-eft', value: 'eft', label: 'Manual EFT', desc: 'Bank transfer — confirmation required' },
                  ].map((method) => {
                    const isSelected = watch('paymentMethod') === method.value;
                    return (
                      <label
                        key={method.id}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                          isSelected
                            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40'
                        }`}
                      >
                        <input
                          type="radio"
                          value={method.value}
                          {...register('paymentMethod')}
                          className="w-4 h-4 accent-primary"
                        />
                        <div>
                          <p className="text-sm font-semibold">{method.label}</p>
                          <p className="text-xs text-muted-foreground">{method.desc}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* BACKEND INTEGRATION: PayFast/PayGate redirect goes here */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 leading-relaxed">
                <strong>Note:</strong> After submitting, you will receive a payment link via WhatsApp
                and email within 24 hours. Your child's benefits activate from the date of first
                successful payment.
              </div>
            </div>
          )}

          {/* ─── Navigation buttons ───────────────────────────────── */}
          <div className="px-6 py-4 border-t border-border flex items-center justify-between bg-secondary/30">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
            >
              <ChevronLeft size={16} />
              Back
            </button>

            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 shadow-sm"
              >
                Continue
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting || selectedCount < 3}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-accent text-accent-foreground text-sm font-bold hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 shadow-sm min-w-[160px] justify-center"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    Submit Enrollment
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Required fields note */}
      <p className="text-xs text-muted-foreground mt-4 text-center">
        Fields marked <span className="text-red-500">*</span> are required.
        Your data is handled in accordance with POPIA and CPA requirements.
      </p>
    </div>
  );
}